import React, { useState, useEffect } from 'react';

const DatabaseConfigForm = ({ subProject, onConfigSaved, onCancel }) => {
  const [config, setConfig] = useState({
    database_type: 'none',
    database_host: '',
    database_port: '',
    database_name: '',
    database_user: '',
    database_password: '',
    database_ssl_enabled: true,
    database_connection_string: ''
  });

  const [testResult, setTestResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Load existing configuration
    if (subProject?.id) {
      fetch(`/api/projects/sub-projects/${subProject.id}/database-config`)
        .then(res => res.json())
        .then(data => {
          setConfig(prev => ({
            ...prev,
            database_type: data.database_type || 'none',
            database_host: data.database_host || '',
            database_port: data.database_port || '',
            database_name: data.database_name || '',
            database_user: data.database_user || '',
            database_ssl_enabled: data.database_ssl_enabled !== undefined ? data.database_ssl_enabled : true,
            database_connection_string: data.database_connection_string || ''
          }));
        })
        .catch(err => console.error('Failed to load database config:', err));
    }
  }, [subProject?.id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(null);
    setTestResult(null);
  };

  const handleTestConnection = async () => {
    if (!subProject?.id) return;

    setIsTesting(true);
    setError(null);
    setTestResult(null);

    try {
      // First save the config, then test
      const saveResponse = await fetch(`/api/projects/sub-projects/${subProject.id}/database-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (!saveResponse.ok) {
        throw new Error('Failed to save configuration');
      }

      // Now test the connection
      const testResponse = await fetch(`/api/projects/sub-projects/${subProject.id}/database-config/test`, {
        method: 'POST'
      });

      const result = await testResponse.json();
      setTestResult(result);

      if (!result.success) {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message || 'Failed to test connection');
      setTestResult({ success: false, message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (!subProject?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/projects/sub-projects/${subProject.id}/database-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      const result = await response.json();
      if (onConfigSaved) {
        onConfigSaved(result);
      }
    } catch (err) {
      setError(err.message || 'Failed to save configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultPort = (dbType) => {
    switch(dbType) {
      case 'postgresql': return 5432;
      case 'mysql': return 3306;
      case 'mongodb': return 27017;
      case 'sqlite': return '';
      default: return '';
    }
  };

  const handleDatabaseTypeChange = (e) => {
    const newType = e.target.value;
    setConfig(prev => ({
      ...prev,
      database_type: newType,
      database_port: getDefaultPort(newType)
    }));
    setError(null);
    setTestResult(null);
  };

  const showConnectionFields = config.database_type !== 'none' && config.database_type !== 'sqlite';
  const showSQLiteField = config.database_type === 'sqlite';

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl mx-auto">
      <h2 className="text-xl font-bold mb-4">
        Database Configuration: {subProject?.name}
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {testResult && testResult.success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
          ✓ Connection successful! {testResult.database_type} {testResult.version}
        </div>
      )}

      <div className="space-y-4">
        {/* Database Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Database Type
          </label>
          <select
            name="database_type"
            value={config.database_type}
            onChange={handleDatabaseTypeChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="none">None</option>
            <option value="postgresql">PostgreSQL</option>
            <option value="mysql">MySQL</option>
            <option value="sqlite">SQLite</option>
            <option value="mongodb">MongoDB</option>
          </select>
        </div>

        {showConnectionFields && (
          <>
            {/* Host */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Host
              </label>
              <input
                type="text"
                name="database_host"
                value={config.database_host}
                onChange={handleInputChange}
                placeholder="localhost or database hostname"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Port */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Port
              </label>
              <input
                type="number"
                name="database_port"
                value={config.database_port}
                onChange={handleInputChange}
                placeholder={getDefaultPort(config.database_type)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Database Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Database Name
              </label>
              <input
                type="text"
                name="database_name"
                value={config.database_name}
                onChange={handleInputChange}
                placeholder="database name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                name="database_user"
                value={config.database_user}
                onChange={handleInputChange}
                placeholder="database username"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                name="database_password"
                value={config.database_password}
                onChange={handleInputChange}
                placeholder="database password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Password will be stored securely
              </p>
            </div>

            {/* SSL Enabled */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="database_ssl_enabled"
                checked={config.database_ssl_enabled}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-700">
                Enable SSL/TLS
              </label>
            </div>
          </>
        )}

        {showSQLiteField && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Database File Path
            </label>
            <input
              type="text"
              name="database_name"
              value={config.database_name}
              onChange={handleInputChange}
              placeholder="/path/to/database.db"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {config.database_type === 'mongodb' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Connection String (Optional)
            </label>
            <input
              type="text"
              name="database_connection_string"
              value={config.database_connection_string}
              onChange={handleInputChange}
              placeholder="mongodb://username:password@host:port/database"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              If provided, this will be used instead of individual fields
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="mt-6 flex gap-3">
        <button
          onClick={handleSave}
          disabled={isLoading || config.database_type === 'none'}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Saving...' : 'Save Configuration'}
        </button>

        <button
          onClick={handleTestConnection}
          disabled={isTesting || config.database_type === 'none'}
          className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {isTesting ? 'Testing...' : 'Test Connection'}
        </button>

        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

export default DatabaseConfigForm;
